import type { MaybeRef } from "vue";

export type SubpagesOptions<T extends string, A extends object> = {
  rootPath: string;
  default?: T;
  paths: ({ value: T; name: string } & A)[];
};

function joinPaths(...paths: string[]) {
  return paths
    .join("/")
    .replace(/\/{2,}/g, "/") /* '//' -> '/' */
    .replace(/\/$/, ""); /* remove trailing '/' */
}

// TODO: make prop reactive
export function useSubpaths<T extends string, A extends object>(
  options: MaybeRefOrGetter<SubpagesOptions<T, A>>,
) {
  const route = useRoute();

  function getFullPath(subpath: string) {
    return joinPaths(toValue(options).rootPath, subpath);
  }

  function isCurrentRouteOnSubpath(subpath: string) {
    const fullPath = getFullPath(subpath);

    // this case handles a subpath being the root path
    if (fullPath === toValue(options).rootPath)
      return route.path.split("?")[0] === fullPath.split("?")[0];

    return route.path.indexOf(fullPath.split("?")[0]) === 0;
  }

  onMounted(() => {
    const isAlreadyOnASubpath = toValue(options).paths.some((path) =>
      isCurrentRouteOnSubpath(path.value),
    );

    if (!isAlreadyOnASubpath) {
      const defaultSubpath =
        toValue(options).default ?? toValue(options).paths[0].value;
      const defaultPath = getFullPath(defaultSubpath);

      navigateTo(defaultPath, { replace: true });
    }
  });

  const current = computed(
    () =>
      toValue(options)
        .paths.find((path) => isCurrentRouteOnSubpath(path.value))
        ?.value.split("?")[0] ?? toValue(options).paths[0].value.split("?")[0],
  );

  const paths = computed(() =>
    toValue(options).paths.map((path) => ({
      ...path,
      href: getFullPath(path.value),
    })),
  );

  return {
    current,
    paths,
    goto: (path: T) => navigateTo(getFullPath(path)),
  };
}
